import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { salonApi, type GoogleReviewItemDto } from '../lib/api'
import './HomeGoogleReviews.css'

const ILIA_SLUG = 'ilia-barbershop'
const FALLBACK_REVIEW_URL = 'https://g.page/r/CU3GthwVpSctEBk/review'
const AUTO_MS = 5500
const TEXT_CLAMP = 160

function GoogleMark({ size = 28 }: { size?: number }) {
  return (
    <svg className="hgr-mark" viewBox="0 0 48 48" width={size} height={size} aria-hidden>
      <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

function StarRow({ rating }: { rating: number }) {
  const full = Math.floor(rating)
  const partial = Math.max(0, Math.min(1, rating - full))
  return (
    <div className="hgr-stars" aria-hidden>
      {Array.from({ length: 5 }, (_, i) => {
        const fill = i < full ? 1 : i === full ? partial : 0
        return (
          <span key={i} className="hgr-star">
            <span className="hgr-star-empty">★</span>
            <span className="hgr-star-fill" style={{ width: `${fill * 100}%` }}>★</span>
          </span>
        )
      })}
    </div>
  )
}

function ReviewStars({ rating }: { rating: number }) {
  return (
    <span className="hgr-review-stars" aria-label={`${rating}/5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < rating ? 'is-on' : ''}>★</span>
      ))}
    </span>
  )
}

function ReviewCard({
  review,
  expanded,
  onToggle,
  readMoreLabel,
  readLessLabel,
}: {
  review: GoogleReviewItemDto
  expanded: boolean
  onToggle: () => void
  readMoreLabel: string
  readLessLabel: string
}) {
  const long = review.text.length > TEXT_CLAMP
  const shown = !long || expanded ? review.text : `${review.text.slice(0, TEXT_CLAMP).trim()}…`
  const place = review.branchCity || review.branchName

  return (
    <article className="hgr-review-card">
      <div className="hgr-review-card-head">
        {review.authorPhotoUrl ? (
          <img
            className="hgr-avatar"
            src={review.authorPhotoUrl}
            alt=""
            width={48}
            height={48}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="hgr-avatar is-fallback" aria-hidden>
            {(review.authorName || '?').slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="hgr-review-meta">
          <strong>{review.authorName}</strong>
          <ReviewStars rating={review.rating} />
        </div>
      </div>
      <blockquote>“{shown}”</blockquote>
      {long ? (
        <button type="button" className="hgr-read-more" onClick={onToggle}>
          {expanded ? readLessLabel : readMoreLabel}
        </button>
      ) : null}
      <footer>
        {place ? <span>{place}</span> : null}
        {review.relativeTime ? <em>{review.relativeTime}</em> : null}
      </footer>
    </article>
  )
}

export function HomeGoogleReviews({ salonSlug = ILIA_SLUG }: { salonSlug?: string }) {
  const { t, i18n } = useTranslation()
  const googleQ = useQuery({
    queryKey: ['google-reputation', salonSlug],
    queryFn: () => salonApi.googleReputation(salonSlug),
    staleTime: 120_000,
  })

  const data = googleQ.data?.data
  const rating = data?.rating ?? 4.9
  const count = data?.count ?? 120
  const label = data?.ratingLabel ?? (i18n.language === 'de' ? rating.toFixed(1).replace('.', ',') : rating.toFixed(1))
  const mapsUrl = data?.url || FALLBACK_REVIEW_URL
  const reviewUrl = data?.reviewUrl || data?.url || FALLBACK_REVIEW_URL
  const reviews = useMemo(() => (data?.reviews ?? []).filter((r) => r.text.trim()), [data?.reviews])

  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const touchX = useRef<number | null>(null)
  const rtl = i18n.language === 'ar'

  const maxIndex = Math.max(0, reviews.length - 1)
  const go = useCallback((dir: 1 | -1) => {
    if (reviews.length === 0) return
    setIndex((i) => {
      const next = i + dir
      if (next < 0) return maxIndex
      if (next > maxIndex) return 0
      return next
    })
  }, [maxIndex, reviews.length])

  useEffect(() => {
    if (paused || reviews.length < 2) return
    const id = window.setInterval(() => go(1), AUTO_MS)
    return () => window.clearInterval(id)
  }, [paused, reviews.length, go])

  useEffect(() => {
    setIndex(0)
    setExpandedId(null)
  }, [reviews.length])

  const current = reviews[index]
  const second = reviews.length > 1 ? reviews[(index + 1) % reviews.length] : null

  return (
    <section
      className="home-google-reviews"
      id="google-reviews"
      aria-labelledby="hgr-title"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="container hgr-shell">
        <header className="hgr-head">
          <p className="hgr-eyebrow">{t('homeGoogle.eyebrow')}</p>
          <h2 id="hgr-title">{t('homeGoogle.title')}</h2>
          <p className="hgr-lead">{t('homeGoogle.text')}</p>
        </header>

        {googleQ.isLoading ? <div className="skeleton hgr-skeleton" /> : null}

        <div className="hgr-layout">
          <aside className="hgr-card" aria-label="Google">
            <div className="hgr-card-brand">
              <GoogleMark size={32} />
              <div>
                <strong>Google</strong>
                <span>{t('homeGoogle.basedOn', { count })}</span>
              </div>
            </div>

            <div className="hgr-card-score">
              <em>{label}</em>
              <StarRow rating={rating} />
            </div>

            <a className="hgr-link" href={mapsUrl} target="_blank" rel="noreferrer noopener">
              {rtl ? '← ' : ''}{t('homeGoogle.open')}{rtl ? '' : ' →'}
            </a>

            <a className="btn hgr-rate-btn" href={reviewUrl} target="_blank" rel="noreferrer noopener">
              {t('homeGoogle.cta')}
            </a>
          </aside>

          <div className="hgr-slider-wrap">
            {reviews.length > 0 ? (
              <>
                <div
                  className="hgr-slider"
                  onTouchStart={(e) => {
                    touchX.current = e.changedTouches[0]?.clientX ?? null
                    setPaused(true)
                  }}
                  onTouchEnd={(e) => {
                    const start = touchX.current
                    const end = e.changedTouches[0]?.clientX
                    touchX.current = null
                    if (start == null || end == null) return
                    const delta = end - start
                    if (Math.abs(delta) < 40) return
                    if (rtl) go(delta > 0 ? 1 : -1)
                    else go(delta < 0 ? 1 : -1)
                  }}
                >
                  {current ? (
                    <ReviewCard
                      review={current}
                      expanded={expandedId === current.id}
                      onToggle={() =>
                        setExpandedId((id) => (id === current.id ? null : current.id))
                      }
                      readMoreLabel={t('homeGoogle.readMore')}
                      readLessLabel={t('homeGoogle.readLess')}
                    />
                  ) : null}

                  {second ? (
                    <div className="hgr-quote-desktop">
                      <ReviewCard
                        review={second}
                        expanded={expandedId === second.id}
                        onToggle={() =>
                          setExpandedId((id) => (id === second.id ? null : second.id))
                        }
                        readMoreLabel={t('homeGoogle.readMore')}
                        readLessLabel={t('homeGoogle.readLess')}
                      />
                    </div>
                  ) : null}
                </div>

                {reviews.length > 1 ? (
                  <div className="hgr-nav">
                    <button
                      type="button"
                      className="hgr-nav-btn"
                      aria-label={t('common.back')}
                      onClick={() => {
                        setPaused(true)
                        go(-1)
                      }}
                    >
                      <FontAwesomeIcon icon={rtl ? faChevronRight : faChevronLeft} />
                    </button>
                    <div className="hgr-dots" role="tablist">
                      {reviews.map((r, i) => (
                        <button
                          key={r.id}
                          type="button"
                          role="tab"
                          aria-selected={i === index}
                          className={i === index ? 'is-active' : ''}
                          onClick={() => {
                            setPaused(true)
                            setIndex(i)
                          }}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className="hgr-nav-btn"
                      aria-label={t('common.next')}
                      onClick={() => {
                        setPaused(true)
                        go(1)
                      }}
                    >
                      <FontAwesomeIcon icon={rtl ? faChevronLeft : faChevronRight} />
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}
